import React, { useCallback, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Flex } from 'rebass';
import { useHistory, useLocation } from 'react-router-dom';
import useMode from '../../../../hooks/useMode';
import useNotify from '../../../../hooks/useNotify';
import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import ThirdPane from '../../../../components/ThirdPane';
import ThirdPaneHeader from '../../../../components/ThirdPane/ThirdPaneHeader';
import ThirdPaneContent from '../../../../components/ThirdPane/ThirdPaneContent';
import ThirdPaneFooter from '../../../../components/ThirdPane/ThirdPaneFooter';
import Button from '../../../../components/Button/Button';
import SurveyTemplateForm from '../../../../components/WaterHazard/SurveyTemplateForm';
import HazardSurveyResult, {
  KnownHazardWithProtection,
} from '../../../../components/WaterHazard/HazardSurveyResult';
import {
  CREATE_HAZARD_SURVEY,
  GET_HAZARD_SURVEY_JOB,
  UPDATE_HAZARD_SURVEY,
} from '@/components/WaterHazard/queries';
import { GET_WATER_HAZARDS } from '@/modes/admin/pages/waterHazards/WaterHazards.query';
import { ApolloError } from '@apollo/client/errors';
import { HazardSurveyInput } from '@/components/WaterHazard/types';
import HazardSurveyEditForm from '../../../../components/WaterHazard/HazardSurveyEditForm';
import { Input, Label, Textarea } from '@rebass/forms';
import HazardSurveyTemplatePicker from '../../../../components/WaterHazard/HazardSurveyTemplatePicker';
import WaterServicePicker from '../../../../components/WaterService/WaterServicePicker';
import { GET_WATER_SERVICE_FOR_HAZARD_SURVEY } from '@/components/WaterHazard/queries';
import useSessionInfo from '../../../../hooks/useSessionInfo';
import { gql } from '@/modes/waterSupplier/gql';
import Modal, { ModalBody, ModalFooter } from '../../../../components/Modal';
import Form from '@/components/Form/Form';
import FormColumn from '@/components/Form/FormColumn';

const CREATE_SURVEY_TEMPLATE_MUTATION = gql(`
  mutation SaveAsTemplate($input: WaterHazardSurveyTemplateInput!) {
    createWaterHazardSurveyTemplate(input: $input) {
      id
      name
    }
  }
`);

type HazardSurveyEditorProps = RouteComponentProps<{ id: string }>;

function HazardSurveyEditor({ match }: HazardSurveyEditorProps): JSX.Element {
  const jobId = match.params.id === 'new' ? undefined : match.params.id;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [waterServiceId, setWaterServiceId] = useState(
    searchParams.get('waterServiceId')
  );

  const { showError, showSuccess } = useNotify();
  const mode = useMode();
  const history = useHistory();

  const [survey, setSurvey] = useState<HazardSurveyInput>({
    instructions: '',
    otherHazardsQuestion: false,
    hazardIds: [],
  });

  const { data: existingData, loading: itemLoading } = useQuery(
    GET_HAZARD_SURVEY_JOB,
    {
      skip: !jobId,
      variables: {
        where: { id: jobId },
      },
      onCompleted: (data) => {
        const existing = data?.jobs?.[0]?.waterHazardSurvey;
        const job = data?.jobs?.[0];
        if (!existing || !job) return;
        setSurvey({
          instructions: existing.instructions || '',
          otherHazardsQuestion: !!existing.otherHazardsQuestion,
          protectedQuestion: !!existing.protectedQuestion,
          hazardIds: existing.items.map((i: any) => i.waterHazardId),
          scheduled: job.scheduled
            ? new Date(job.scheduled).toISOString()
            : undefined,
          contactProfileId: existing.contactProfileId || undefined,
          outboundAssignment: job.outboundAssignment
            ? {
                vendorProfileId: job.outboundAssignment.vendorProfile.id,
              }
            : undefined,
        });
        // Load known hazards from the water service when editing
        const wsHazards = (existing.waterService?.waterServiceHazards ??
          []) as any[];
        const existingHazards: KnownHazardWithProtection[] = wsHazards.map(
          (h: any) => ({
            id: h.waterHazardId,
            name: h.waterHazard?.name ?? h.waterHazardId,
            hazardLevel: h.waterHazard?.hazardLevel ?? 'LOW',
            isProtected: h.isProtected ?? null,
            backflowAssemblyId: h.backflowAssemblyId ?? null,
          })
        );
        if (existingHazards.length > 0) {
          setKnownHazards(existingHazards);
        }
      },
    }
  );

  const [selectedWaterService, setSelectedWaterService] = useState<any>(null);
  const [knownHazards, setKnownHazards] = useState<KnownHazardWithProtection[]>(
    []
  );

  const { data: waterServiceData } = useQuery(
    GET_WATER_SERVICE_FOR_HAZARD_SURVEY,
    {
      skip: !!jobId || !waterServiceId,
      variables: { where: { id: waterServiceId } },
      onCompleted: (data) => {
        const ws = data?.waterServices?.[0];
        const existingHazards: KnownHazardWithProtection[] = (
          (ws?.waterServiceHazards ?? []) as any[]
        ).map((h: any) => ({
          id: h.waterHazardId,
          name: h.waterHazard?.name ?? h.waterHazardId,
          hazardLevel: h.waterHazard?.hazardLevel ?? 'LOW',
          isProtected: h.isProtected ?? null,
          backflowAssemblyId: h.backflowAssemblyId ?? null,
        }));
        if (existingHazards.length > 0) {
          setKnownHazards(existingHazards);
        }
      },
    }
  );

  const { data: hazardsData } = useQuery(GET_WATER_HAZARDS);

  const { session } = useSessionInfo();
  const wsProfile = session?.companyProfile?.waterSupplier?.profile;
  const branding = wsProfile
    ? { name: wsProfile.name ?? null, logoUrl: wsProfile.image?.url ?? null }
    : null;

  const [updateSurvey, { loading: saveLoading }] = useMutation<any, any>(
    UPDATE_HAZARD_SURVEY,
    {
      onCompleted: () => {
        showSuccess('Hazard survey saved');
        history.push(`/${mode}/hazard-surveys/${jobId}`);
      },
      onError: (error: ApolloError) => {
        showError('Error saving hazard survey', error);
      },
    }
  );

  const [createSurvey, { loading: createLoading }] = useMutation<any, any>(
    CREATE_HAZARD_SURVEY,
    {
      onCompleted: (data) => {
        showSuccess('Hazard survey created');
        const newJobId = data.createWaterHazardSurvey?.jobId;
        history.push(`/${mode}/hazard-surveys/${newJobId}`);
      },
      onError: (error: ApolloError) => {
        showError('Error creating hazard survey', error);
      },
    }
  );

  // Save as Template dialog
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');

  const [createSurveyTemplate, { loading: saveTemplateLoading }] = useMutation<
    any,
    any
  >(CREATE_SURVEY_TEMPLATE_MUTATION, {
    onCompleted: () => {
      showSuccess('Template saved');
      setSaveAsTemplateOpen(false);
      setTemplateName('');
      setTemplateDescription('');
    },
    onError: (error: ApolloError) => {
      showError('Error saving template', error);
    },
    refetchQueries: ['GetWaterHazardSurveyTemplates', 'GetWSSurveyTemplates'],
  });

  const handleSaveAsTemplate = useCallback(async () => {
    if (!templateName.trim()) {
      showError('Please enter a template name');
      return;
    }
    const knownIds = knownHazards.map((h) => h.id);
    const additionalIds = (survey.hazardIds ?? []).filter(
      (id) => !knownIds.includes(id)
    );
    const allHazardIds = [...knownIds, ...additionalIds];
    await createSurveyTemplate({
      variables: {
        input: {
          name: templateName.trim(),
          description: templateDescription.trim() || undefined,
          instructions: survey.instructions || '',
          otherHazardsQuestion: survey.otherHazardsQuestion ?? false,
          protectedQuestion: survey.protectedQuestion ?? false,
          hazardIds: allHazardIds,
        },
      },
    });
  }, [
    templateName,
    templateDescription,
    survey,
    knownHazards,
    createSurveyTemplate,
    showError,
  ]);

  const handleSave = useCallback(async () => {
    // Merge known hazard IDs with additional picker IDs (deduped)
    const knownIds = knownHazards.map((h) => h.id);
    const additionalIds = (survey.hazardIds ?? []).filter(
      (id) => !knownIds.includes(id)
    );
    const allHazardIds = [...knownIds, ...additionalIds];
    const input = {
      contactProfileId: survey.contactProfileId,
      outboundAssignment: survey.outboundAssignment,
      instructions: survey.instructions,
      otherHazardsQuestion: survey.otherHazardsQuestion,
      protectedQuestion: survey.protectedQuestion,
      scheduled: survey.scheduled
        ? new Date(survey.scheduled).getTime()
        : undefined,
      items: allHazardIds.map((id, index) => ({
        waterHazardId: id,
        displayOrder: index,
      })),
    };

    if (jobId) {
      await updateSurvey({
        variables: {
          jobId,
          input,
        },
      });
    } else {
      await createSurvey({
        variables: {
          waterServiceId,
          input,
        },
      });
    }
  }, [survey, jobId, waterServiceId, knownHazards, updateSurvey, createSurvey]);

  const handleCancel = () => {
    if (jobId) {
      history.push(`/${mode}/hazard-surveys/${jobId}`);
    } else if (waterServiceId) {
      history.push(`/${mode}/water-services/${waterServiceId}`);
    } else {
      history.push(`/${mode}/hazard-surveys`);
    }
  };

  const job = existingData?.jobs?.[0];
  const waterService =
    job?.waterHazardSurvey?.waterService ||
    waterServiceData?.waterServices?.[0] ||
    selectedWaterService;

  return (
    <ThirdPaneLayout paneVisible={true}>
      <MainColumn width="calc(100% - 640px)">
        <Flex flexDirection="column" height="100%">
          <Box bg="gray.100" flexGrow={1} sx={{ overflowY: 'auto' }}>
            <HazardSurveyResult
              pt={4}
              template={{
                instructions: survey.instructions || '',
                otherHazardsQuestion: !!survey.otherHazardsQuestion,
                protectedQuestion: !!survey.protectedQuestion,
                hazardIds: survey.hazardIds || [],
              }}
              hazards={hazardsData?.waterHazards ?? []}
              waterService={waterService as any}
              branding={branding}
              knownHazards={knownHazards}
            />
          </Box>
        </Flex>
      </MainColumn>
      <ThirdPane width={640} visible={true}>
        <ThirdPaneHeader
          title={jobId ? 'Edit Hazard Survey' : 'New Hazard Survey'}
        />
        <ThirdPaneContent p={0}>
          {itemLoading ? (
            <Flex
              justifyContent="center"
              alignItems="center"
              flexGrow={1}
              p={3}
            >
              Loading...
            </Flex>
          ) : (
            <>
              <Box
                sx={{ borderBottom: '1px solid', borderColor: 'tableBorder' }}
              >
                {!jobId && !searchParams.get('waterServiceId') && (
                  <Box mb={3} px={4} pt={3}>
                    <Label>Water Service</Label>
                    <WaterServicePicker
                      waterServiceId={waterServiceId}
                      onChange={(ws) => {
                        setWaterServiceId(ws.id);
                        setSelectedWaterService(ws);
                      }}
                    />
                  </Box>
                )}
                <Box mb={3}>
                  <HazardSurveyEditForm
                    value={survey}
                    onChange={(updated) =>
                      setSurvey((prev) => ({ ...prev, ...updated }))
                    }
                    siteAddressId={waterService?.address?.id}
                    waterServiceId={waterServiceId}
                  />
                </Box>
              </Box>
              <Box mt={4} px={4}>
                <Label>Load template</Label>
                <HazardSurveyTemplatePicker
                  onChange={(template) => {
                    if (template) {
                      setSurvey({
                        ...survey,
                        instructions: template.instructions || '',
                        otherHazardsQuestion:
                          template.otherHazardsQuestion ?? undefined,
                        hazardIds: template.items.map((i) => i.waterHazardId),
                      });
                    }
                  }}
                />
              </Box>
              <SurveyTemplateForm
                hideNameDescription
                template={{
                  instructions: survey.instructions || '',
                  otherHazardsQuestion: !!survey.otherHazardsQuestion,
                  protectedQuestion: !!survey.protectedQuestion,
                  hazardIds: survey.hazardIds || [],
                }}
                onChange={(t) => setSurvey((prev) => ({ ...prev, ...t }))}
                knownHazards={knownHazards}
              />
            </>
          )}
        </ThirdPaneContent>
        <ThirdPaneFooter justifyContent="space-between">
          <Flex sx={{ gap: 2 }}>
            <Button
              variant="primary"
              icon="save"
              onClick={handleSave}
              disabled={
                saveLoading || createLoading || (!jobId && !waterServiceId)
              }
            >
              {saveLoading || createLoading
                ? 'Saving...'
                : jobId
                  ? 'Save Changes'
                  : 'Create Survey'}
            </Button>
            <Button
              variant="outline"
              icon="bookmark"
              onClick={() => {
                setTemplateName('');
                setTemplateDescription('');
                setSaveAsTemplateOpen(true);
              }}
              title="Save current settings as a reusable template"
            >
              Save as Template
            </Button>
          </Flex>
          <Button variant="outline" icon="slash-circle" onClick={handleCancel}>
            Cancel
          </Button>
        </ThirdPaneFooter>
        {saveAsTemplateOpen && (
          <Modal
            title="Save as Template"
            onClose={() => setSaveAsTemplateOpen(false)}
          >
            <ModalBody>
              <Form>
                <FormColumn>
                  <Label htmlFor="template-name-input">Name</Label>
                  <Input
                    id="template-name-input"
                    value={templateName}
                    width="100%"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setTemplateName(e.target.value)
                    }
                    placeholder="Enter template name"
                    autoFocus
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Escape') setSaveAsTemplateOpen(false);
                    }}
                  />
                </FormColumn>
                <FormColumn>
                  <Label htmlFor="template-description-input">
                    Description
                  </Label>
                  <Textarea
                    id="template-description-input"
                    mt={2}
                    value={templateDescription}
                    width="100%"
                    height={54}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setTemplateDescription(e.target.value)
                    }
                    placeholder="Description (optional)"
                  />
                </FormColumn>
              </Form>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="outline"
                onClick={() => setSaveAsTemplateOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveAsTemplate}
                disabled={saveTemplateLoading || !templateName.trim()}
              >
                {saveTemplateLoading ? 'Saving...' : 'Save Template'}
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </ThirdPane>
    </ThirdPaneLayout>
  );
}

export default withRouter(HazardSurveyEditor);
